import { useConsultationLink } from '@/router/hooks/useConsolutationLink';
import { useMdBreakpoint } from '@/theme/hooks/useMdBreakpoint';
import { Button, Modal, Text, useMantineTheme } from '@mantine/core';
import DiagonalLayout from './DiagonalLayout';
import { PrimaryButton } from './PrimaryButton';
import { RedirectToBookAppointmentButton } from './RedirectToBookAppointmentButton';

type SalesModalProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  stepId: string;
  continueButtonType?: 'button' | 'submit' | 'reset';
  formName?: string;
};

const ConsultModal = ({
  opened,
  onClose,
  title,
  stepId,
  continueButtonType = 'button',
  formName,
}: SalesModalProps) => {
  const theme = useMantineTheme();
  const isMdBreakpoint = useMdBreakpoint();
  const consultationLink = useConsultationLink({ stepId });

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      centered
      size="auto"
    >
      <Modal.Overlay
        color={theme.colors.primary[0]}
        opacity={0.85}
      />
      <Modal.Content
        radius={16}
        px="lg"
        py="lg"
        w={900}
        bg={theme.colors.minor[2]}
      >
        <Modal.Header bg={theme.colors.minor[2]}>
          <Modal.Title
            style={{
              color: theme.colors.primary[0],
              fontSize: 22,
              fontWeight: 700,
              margin: '0 auto',
              marginTop: '20px',
            }}
          >
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body mt={16}>
          <DiagonalLayout />

          <RedirectToBookAppointmentButton
            appointmentUrl={consultationLink}
            shouldOpenCallingApp={false}
            stepId={stepId}
            fullWidth
            m="0 auto"
          >
            <PrimaryButton
              type="button"
              size="md"
              isInverted
              styles={(theme) => ({
                root: {
                  borderColor: theme.colors.primary[0],
                  color: 'white',
                  minWidth: !isMdBreakpoint ? '100%' : 345,
                  transition: 'background 250ms linear, border 250ms linear, color 250ms linear',
                  '&:hover': {
                    backgroundColor: theme.colors.secondary[0],
                    borderColor: theme.colors.secondary[0],
                    color: theme.colors.primary[0],
                  },
                },
              })}
            >
              Schedule a Call
            </PrimaryButton>
          </RedirectToBookAppointmentButton>

          <Button
            variant="subtle"
            onClick={onClose}
            fullWidth
            type={continueButtonType}
            form={formName}
            m="0 auto"
            mt={16}
            sx={(t) => ({
              fontWeight: 700,
              color: theme.colors.primary[0],
              background: 'transparent',
              textTransform: 'none',
              fontSize: t.fontSizes.md,
              ':hover': {
                background: 'transparent',
              },
            })}
          >
            Continue Online
          </Button>

          <Text
            size="sm"
            color="dimmed"
            align="center"
            mt="sm"
            sx={{ fontStyle: 'italic', width: '100%' }}
          >
            Consultations are completed with real humans with extensive experience dealing with
            cases similar to yours.
          </Text>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default ConsultModal;
